import { SyntheticEvent, useEffect, useState } from "react";
import { Storage } from "aws-amplify";
import ImageGallery, { ReactImageGalleryItem } from "react-image-gallery";
import { FcDownload } from "react-icons/fc";
import { FaDownload } from "react-icons/fa";
import Select from "react-select";
import { MediaUri, downloadResolutionOptions } from "../../constants/enum";
import ModalConfirm from "../../components/ModalConfirm";
import { downloadBlob } from "../../helpers/download";
import { compareImageFileName } from "../../helpers/sortImage";
import { LoadingImage, NoImageBox } from "./imagePlaceholder";

const PAGE_SIZE = 100;

// รูป PK / CS (Photo) เก็บที่ files/{display|thumbnail}/{materialCode}/{fileName}
// (ต่างจากของเดิมที่เป็น {barcode}/{materialCode}/{ประเภท}/{fileName})
// list จาก thumbnail แล้วตัด prefix ออกเพื่อเอา path ไปต่อกับ MediaUri
const PK_CS_PHOTO_LIST_PREFIX = "files/thumbnail";

// ตรงกับ ALLOW_FILE_EXTENSIONS ของ lambda ที่ resize รูป
// (amplify/backend/function/S3Trigger8b7121ac/src/index.js) นามสกุลอื่นไม่มีไฟล์ให้แสดงอยู่แล้ว
// ใช้กันไฟล์ขยะใน s3 (.DS_Store ฯลฯ) ไม่ให้เข้า gallery
const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png"];

/** shape ของ item ที่ Storage.list คืนมา (S3ProviderListOutputItem) */
type S3ListItem = {
  key?: string;
  eTag?: string;
  lastModified?: Date;
  size?: number;
};

/**
 * shape ของผลลัพธ์ Storage.get(..., { download: true }) เฉพาะส่วนที่ใช้
 * ต้อง cast เอง เพราะ overload ของ amplify v5 resolve เป็น string ในบาง ts version
 */
type S3DownloadOutput = {
  Body?: Blob;
};

type PkCsPhotoImage = {
  path: string; // path หลังตัด prefix : {materialCode}/{fileName}
  name: string; // ชื่อไฟล์จริงที่ list ได้จาก s3
};

export type PkCsPhotoImagesState = {
  images: PkCsPhotoImage[];
  loading: boolean;
};

/**
 * ดึงรายการรูป PK / CS (Photo) จาก s3
 * แยกเป็น hook เพราะผลลัพธ์ต้องใช้ 2 ที่ที่อยู่คนละคอลัมน์กัน
 * - ซ้าย  : <PkCsPhotoGallery /> เอาไปแสดงรูป
 * - ขวา   : panel ของ tab PK / CS (Photo) เอาไปเลือกว่าจะโชว์ InfoCard หรือข้อความว่าไม่มีข้อมูล
 * ยกมาไว้ที่หน้าแม่แล้วส่งผลชุดเดียวกันลงไป จึงยิง s3 ครั้งเดียว
 * ส่ง materialCode เป็น undefined = ยังไม่ต้องยิง (หน้าแม่ใช้หน่วงไว้จนกด tab ครั้งแรก)
 */
export function usePkCsPhotoImages(
  materialCode?: string | null
): PkCsPhotoImagesState {
  const [images, setImages] = useState<PkCsPhotoImage[]>([]);
  // เก็บ materialCode ที่ fetch เสร็จแล้ว เพื่อคิด loading สด ๆ ตอน render
  // ถ้าใช้ state loading + setLoading ใน effect จะมีเฟรมแรกที่ยังเป็น false
  // ทำให้ขึ้น "ไม่มีข้อมูล" แวบนึงก่อน spinner
  const [loadedCode, setLoadedCode] = useState<string | null>(null);
  const loading = materialCode ? loadedCode !== materialCode : false;

  useEffect(() => {
    let cancelled = false;

    const fetchPkCsPhotoImages = async (materialCode: string): Promise<void> => {
      try {
        const prefix = `${PK_CS_PHOTO_LIST_PREFIX}/${materialCode}/`;
        const { results } = await Storage.list(prefix, {
          level: "public",
          pageSize: PAGE_SIZE,
        });

        // ชื่อไฟล์เอาจาก s3 ทั้งหมด ไม่ได้เดาจาก convention
        const pkCsPhotoImages: PkCsPhotoImage[] = (results as S3ListItem[])
          .filter((item: S3ListItem): item is S3ListItem & { key: string } => {
            if (!item || !item.key) return false;
            if (!item.size || item.size === 0) return false; // ตัด folder marker
            const lower = item.key.toLowerCase();
            return IMAGE_EXTENSIONS.some((ext: string) => lower.endsWith(ext));
          })
          .map((item): PkCsPhotoImage => {
            const path = item.key.replace(`${PK_CS_PHOTO_LIST_PREFIX}/`, "");
            const segments = path.split("/");
            return { path, name: segments[segments.length - 1] };
          })
          .sort((a: PkCsPhotoImage, b: PkCsPhotoImage) =>
            compareImageFileName(a.name, b.name)
          );

        if (!cancelled) {
          setImages(pkCsPhotoImages);
        }
      } catch (error) {
        console.log("fetch pk / cs photo image(s) error : ", error);
        if (!cancelled) {
          setImages([]); // list พัง -> ถือว่าไม่มีรูป ไม่ให้หน้าพัง
        }
      } finally {
        if (!cancelled) {
          setLoadedCode(materialCode);
        }
      }
    };

    if (materialCode) {
      fetchPkCsPhotoImages(materialCode);
    } else {
      setImages([]);
      setLoadedCode(null);
    }

    return () => {
      cancelled = true;
    };
  }, [materialCode]);

  return { images, loading };
}

function PkCsPhotoGallery({ images, loading }: PkCsPhotoImagesState) {
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [downloadResolution, setDownloadResolution] = useState<string>(
    downloadResolutionOptions[0].value
  );

  /**
   * ชื่อไฟล์ที่ได้ตอน download ให้อยู่รูปแบบเดียวกับ tab ea : {RES}-PK-CS-{ชื่อไฟล์}.{นามสกุล}
   * ea เก็บ name ไว้แบบไม่มีนามสกุลแล้วปล่อยให้เบราว์เซอร์เติมให้ แต่ PK / CS (Photo) ได้ชื่อเต็มจาก s3
   * จึงต้องตัดนามสกุลออกก่อนแล้วต่อกลับเอง ไม่ให้ได้ .png ซ้อนสองชั้น
   */
  const getDownloadFileName = (
    img: PkCsPhotoImage,
    resolution: string
  ): string => {
    if (!img.name) return `${resolution}-PK-CS-_.png`;

    const dotIndex = img.name.lastIndexOf(".");
    const baseName = dotIndex > 0 ? img.name.slice(0, dotIndex) : img.name;
    const extension = dotIndex > 0 ? img.name.slice(dotIndex) : "";

    return `${resolution}-PK-CS-${baseName}${extension}`;
  };

  /** map resolution -> โฟลเดอร์ใน s3 ชุดเดียวกับ tab ea */
  const getDownloadSource = (resolution: string): string => {
    return resolution === "HIGH"
      ? "downloads/large"
      : resolution === "MEDIUM"
      ? "downloads/medium"
      : resolution === "LOW"
      ? "downloads/small"
      : "original";
  };

  const downloadPkCsPhotoImage = async (
    img: PkCsPhotoImage,
    resolution: string
  ): Promise<void> => {
    try {
      // โหลดผ่าน Storage.get (ยิงตรงไป s3 ที่เปิด cors ไว้) เหมือน tab ea
      // fetch จาก cdn ไม่ได้ เพราะ response ที่ cache ไว้ไม่มี header access-control-allow-origin
      const key = `${getDownloadSource(resolution)}/${img.path}`;
      const result = (await Storage.get(key, {
        download: true,
      })) as unknown as S3DownloadOutput;

      if (!result.Body) {
        console.log("download pk / cs photo image : empty body ", key);
        return;
      }

      downloadBlob(result.Body, getDownloadFileName(img, resolution));
    } catch (error) {
      console.log("download pk / cs photo image error : ", error);
    }
  };

  const handleImageError = (
    event: SyntheticEvent<HTMLImageElement, Event>
  ): void => {
    event.currentTarget.src = "/brands/no_image.png";
  };

  const pkCsPhotoImageObjects: ReactImageGalleryItem[] = images.map(
    (img: PkCsPhotoImage, index: number) => ({
      original: `${MediaUri}/display/${img.path}`,
      thumbnail: `${MediaUri}/thumbnail/${img.path}`,
      description: img.name,

      renderItem: () => (
        <div className="flex flex-row justify-center">
          <div className="px-8 py-4">
            <div className="relative" data-te-dropdown-ref>
              <button
                className="float-left p-2 bg-white rounded-full shadow-lg hover:bg-slate-400 shadow-slate-400"
                type="button"
                id={`pkCsPhotoDropdownMenuButton${index}`}
                data-te-dropdown-toggle-ref
                aria-expanded="false"
                data-te-ripple-init
                data-te-ripple-color="light"
              >
                <FcDownload size={25} />
              </button>
              <ul
                className="absolute z-[1000] float-left m-0 hidden min-w-max list-none overflow-hidden rounded-lg border-none bg-white bg-clip-padding text-left text-base shadow-lg dark:bg-neutral-700 [&[data-te-dropdown-show]]:block"
                aria-labelledby={`pkCsPhotoDropdownMenuButton${index}`}
                data-te-dropdown-menu-ref
              >
                {downloadResolutionOptions.map((op: any) => {
                  return (
                    <li key={op.value}>
                      <button
                        className="block w-full px-4 py-2 text-sm font-bold bg-transparent whitespace-nowrap text-neutral-700 hover:bg-neutral-100 active:text-neutral-800 active:no-underline disabled:pointer-events-none disabled:bg-transparent disabled:text-neutral-400 dark:text-neutral-200 dark:hover:bg-neutral-600"
                        data-te-dropdown-item-ref
                        onClick={() => downloadPkCsPhotoImage(img, op.value)}
                      >
                        <span>{`${op.label}`}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            <img
              className="object-contain w-48 rounded-t-lg h-fit md:w-96"
              src={`${MediaUri}/display/${img.path}`}
              alt={img.name}
              onError={handleImageError}
            />
          </div>
        </div>
      ),
    })
  );

  if (loading) {
    return <LoadingImage />;
  }

  if (images.length === 0) {
    // กล่องเดียวกับฝั่ง ea ตอนไม่มีไฟล์ ส่วน text อยู่ที่ panel ฝั่งขวา
    return <NoImageBox />;
  }

  return (
    <>
      <ModalConfirm
        show={showDialog}
        onHide={() => {}}
        title="Download PK / CS (Photo) file(s)"
        cancelText={"Close"}
        cancelAction={async () => {
          await setShowDialog(false);
        }}
        okClass="btn-submit"
        okText="Download"
        okAction={async () => {
          await Promise.all(
            images.map((img: PkCsPhotoImage) =>
              downloadPkCsPhotoImage(img, downloadResolution)
            )
          );
          await setShowDialog(false);
        }}
        maxWidth="max-w-4xl"
      >
        {showDialog ? (
          <div className="overflow-auto h-96">
            <div className="flex flex-row justify-center px-4 py-2">
              <div className="w-full">
                <Select
                  options={downloadResolutionOptions}
                  defaultValue={downloadResolutionOptions[0]}
                  onChange={(e: any) => {
                    setDownloadResolution(e.value);
                  }}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {images.map((img: PkCsPhotoImage) => {
                return (
                  <div key={img.path} className="flex flex-row m-2">
                    <img
                      className="object-contain h-24"
                      src={`${MediaUri}/thumbnail/${img.path}`}
                      alt={img.name}
                      onError={handleImageError}
                    />
                    <div className="grid justify-between grid-cols-1">
                      <span className="font-bold">{img.name}</span>
                      <div className="grid grid-cols-1 gap-0">
                        <span className="text-xs">PK / CS</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex flex-row justify-center">
            <span>No Data</span>
          </div>
        )}
      </ModalConfirm>

      <div className="flex flex-row justify-center">
        <div className="object-contain w-4/5 py-4 h-fit">
          <ImageGallery
            items={pkCsPhotoImageObjects}
            infinite={false}
            showBullets={true}
            showIndex={true}
            showFullscreenButton={true}
            showPlayButton={false}
            onErrorImageURL="/brands/no_image.png"
          />
          <div className="flex flex-row justify-center mt-8">
            <button
              type="button"
              onClick={() => {
                setShowDialog(true);
              }}
              disabled={images.length === 0}
              data-te-ripple-init
              data-te-ripple-color="light"
              className="disabled:bg-slate-500  inline-block rounded bg-primary px-6 pb-2 pt-2.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#3b71ca] transition duration-150 ease-in-out hover:bg-primary-600 hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:bg-primary-600 focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:outline-none focus:ring-0 active:bg-primary-700 active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] dark:shadow-[0_4px_9px_-4px_rgba(59,113,202,0.5)] dark:hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)]"
            >
              <span className="flex flex-row justify-center gap-2">
                <FaDownload size={15} /> <span> Download all file(s)</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default PkCsPhotoGallery;
